import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Check, Plus, Sparkles, MessageCircle, Settings } from 'lucide-react';
import partnerService from '../services/partnerService';
import dmService from '../services/dmService';
import { activityService } from '../services/activityService';
import tokenService from '../services/tokenService';
import PartnerFormContent from './PartnerFormContent';
import './DmPartnerSetupWizard.css';
import './PartnerFormContent.css';

const DmPartnerSetupWizard = ({ open, onClose, onComplete, currentPartnerId, user }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [partners, setPartners] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null); // 'create' or 'select'
  const [selectedPartnerId, setSelectedPartnerId] = useState(currentPartnerId);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [newPartnerData, setNewPartnerData] = useState({
    name: '',
    description: '',
    category: 'general',
    tags: '',
    primaryTask: '在私訊中協助使用者進行對話和思考',
    coreMotivation: '',
    openingApproach: '',
    responseExamples: [''],
    professionalSkills: '',
    backgroundKnowledge: '',
    keyTerminology: '',
    personality: '友善、耐心、支持性',
    communicationStyleDetail: '溫和、鼓勵式，適合一對一交流',
    interactionMethod: '使用問題導向為主的對話框架，適合引導使用者思考和學習',
    guardrails: '',
    responseMode: 'always',
    responseConditions: '',
    availabilitySchedule: '',
    responseDelay: '',
    isPublicTemplate: false
  });

  const TOTAL_STEPS = 3;

  useEffect(() => {
    if (open) {
      loadPartners();
      // 如果已有夥伴，默認選擇選擇現有夥伴
      if (currentPartnerId) {
        setSelectedOption('select');
        setSelectedPartnerId(currentPartnerId);
      }
    }
  }, [open, currentPartnerId]);

  const loadPartners = async () => {
    try {
      const response = await partnerService.getAllPartners(1, 100);
      setPartners(response.partners || response.data || []);
    } catch (err) {
      console.error('載入夥伴列表失敗:', err);
      setError('無法載入夥伴列表');
    }
  };

  const handleNext = () => {
    if (step === 1 && !selectedOption) {
      setError('請選擇一個選項');
      return;
    }
    if (step === 2) {
      if (selectedOption === 'select' && !selectedPartnerId) {
        setError('請選擇一個夥伴');
        return;
      }
      if (selectedOption === 'create' && !newPartnerData.name.trim()) {
        setError('請輸入夥伴名稱');
        return;
      }
    }
    setError(null);
    setStep(step + 1);
  };

  const handleBack = () => {
    setError(null);
    setStep(step - 1);
  };

  const handleComplete = async () => {
    try {
      setLoading(true);
      setError(null);
      const userId = user?.id || localStorage.getItem('userId');
      if (!userId) {
        throw new Error('無法獲取用戶 ID');
      }
      let partner = null;

      // 創建或獲取夥伴
      if (selectedOption === 'create') {
        partner = await partnerService.createPartner({
          name: newPartnerData.name,
          description: newPartnerData.description,
          category: newPartnerData.category,
          tags: newPartnerData.tags,
          roleTask: {
            primaryTask: newPartnerData.primaryTask,
            coreMotivation: newPartnerData.coreMotivation,
            openingApproach: newPartnerData.openingApproach,
            responseExamples: newPartnerData.responseExamples.filter(x => x.trim())
          },
          knowledgeBase: {
            professionalSkills: newPartnerData.professionalSkills || '',
            backgroundKnowledge: newPartnerData.backgroundKnowledge,
            keyTerminology: newPartnerData.keyTerminology ? newPartnerData.keyTerminology.split('\n').filter(x => x.trim()) : []
          },
          communicationStyle: {
            personality: newPartnerData.personality,
            communicationStyle: newPartnerData.communicationStyleDetail,
            interactionMethod: newPartnerData.interactionMethod,
            guardrails: newPartnerData.guardrails ? newPartnerData.guardrails.split('\n').filter(x => x.trim()) : []
          },
          responseTimeline: {
            responseMode: newPartnerData.responseMode,
            responseConditions: newPartnerData.responseConditions,
            availabilitySchedule: newPartnerData.availabilitySchedule,
            responseDelay: newPartnerData.responseDelay
          },
          isPublicTemplate: newPartnerData.isPublicTemplate
        });
      } else {
        partner = partners.find(p => p.id === selectedPartnerId);
        if (!partner) throw new Error('找不到選擇的夥伴');
      }

      // 更新 DM 設定並獲取活動 ID
      const dmSettings = await dmService.updateDmSettings(userId, {
        dmOpen: true,
        partnerId: partner.id
      });

      console.log('DM 設定更新成功:', dmSettings);

      // 更新 token 以確保有新活動的權限
      await tokenService.renewToken();

      // 將夥伴加入到活動（如果有活動 ID）
      const activityId = dmSettings.dmActivityId || dmSettings.activityId;
      if (activityId) {
        console.log('正在將夥伴添加到活動:', {
          partnerId: partner.id,
          partnerName: partner.name,
          activityId: activityId
        });
        
        // 如果有舊夥伴，先清除
        if (currentPartnerId && currentPartnerId !== partner.id) {
          console.log('清除舊夥伴:', currentPartnerId);
          await activityService.removePartnerFromActivity(activityId, currentPartnerId)
            .catch(err => console.warn('清除舊夥伴失敗（可能不存在）:', err));
        }
        
        const result = await activityService.addPartnerToActivity(
          partner.id,
          partner.name,
          activityId,
          {
            description: partner.description || '',
            category: partner.category || 'general',
            tags: partner.tags || ''
          }
        );

        if (result.success) {
          console.log('夥伴成功添加到活動:', result.data);
        } else {
          console.error('添加夥伴到活動失敗:', result.error);
          throw new Error(result.error || '無法將夥伴添加到活動');
        }
      } else {
        console.warn('DM 設定中沒有活動 ID，無法添加夥伴到活動', dmSettings);
      }

      onComplete(partner.id);
      resetWizard();
    } catch (err) {
      console.error('設定失敗:', err);
      setError(err.response?.data?.message || err.message || '設定失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setSelectedOption(null);
    setSelectedPartnerId(currentPartnerId);
    setShowAdvancedSettings(false);
    setNewPartnerData({
      name: '',
      description: '',
      category: 'general',
      tags: '',
      primaryTask: '在私訊中協助使用者進行對話和思考',
      coreMotivation: '',
      openingApproach: '',
      responseExamples: [''],
      professionalSkills: '',
      backgroundKnowledge: '',
      keyTerminology: '',
      personality: '友善、耐心、支持性',
      communicationStyleDetail: '溫和、鼓勵式，適合一對一交流',
      interactionMethod: '使用問題導向為主的對話框架，適合引導使用者思考和學習',
      guardrails: '',
      responseMode: 'always',
      responseConditions: '',
      availabilitySchedule: '',
      responseDelay: '',
      isPublicTemplate: false
    });
    setError(null);
  };

  const handleSkip = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('userId');
      await dmService.updateDmSettings(userId, {
        dmOpen: true,
        partnerId: null
      });
      onComplete(null);
      resetWizard();
    } catch (err) {
      console.error('跳過設定失敗:', err);
      setError('操作失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="wizard-overlay" onClick={onClose}>
      <div className="wizard-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="wizard-header">
          <div className="wizard-header-content">
            <MessageCircle size={24} className="wizard-icon" />
            <h2>設定私訊夥伴</h2>
          </div>
          
          {/* Progress Bar */}
          <div className="wizard-progress">
            <div className="wizard-progress-bar">
              <div 
                className="wizard-progress-fill" 
                style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
              />
            </div>
            <div className="wizard-progress-text">
              步驟 {step} / {TOTAL_STEPS}
            </div>
          </div>
          
          <button className="wizard-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="wizard-content">
          {error && (
            <div className="wizard-error">
              {error}
            </div>
          )}

          {/* Step 1: 選擇模式 */}
          {step === 1 && (
            <div className="wizard-step">
              <div className="wizard-options">
                <div 
                  className={`wizard-option-card ${selectedOption === 'create' ? 'selected' : ''}`}
                  onClick={() => setSelectedOption('create')}
                >
                  <div className="option-icon">
                    <Plus size={32} />
                  </div>
                  <h4>創建新夥伴</h4>
                  <p>為私訊專門設計一個全新的 AI 夥伴</p>
                </div>

                <div 
                  className={`wizard-option-card ${selectedOption === 'select' ? 'selected' : ''}`}
                  onClick={() => setSelectedOption('select')}
                >
                  <div className="option-icon">
                    <Sparkles size={32} />
                  </div>
                  <h4>選擇現有夥伴</h4>
                  <p>從您已創建的夥伴中選擇一個</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: 根據選擇顯示不同內容 */}
          {step === 2 && selectedOption === 'create' && (
            <div className="wizard-step">
              <div className="wizard-form-wrapper">
                <PartnerFormContent
                  formData={newPartnerData}
                  onInputChange={(e) => {
                    const { name, value, type, checked } = e.target;
                    setNewPartnerData(prev => ({
                      ...prev,
                      [name]: type === 'checkbox' ? checked : value
                    }));
                  }}
                  showAdvancedSettings={showAdvancedSettings}
                  onToggleAdvanced={() => setShowAdvancedSettings(!showAdvancedSettings)}
                  compact={true}
                  theme="light"
                />
              </div>
            </div>
          )}

          {step === 2 && selectedOption === 'select' && (
            <div className="wizard-step">
              {loading ? (
                <div className="wizard-loading">載入中...</div>
              ) : partners.length === 0 ? (
                <div className="wizard-empty">
                  <p>您還沒有創建任何夥伴</p>
                  <button 
                    className="wizard-btn-secondary"
                    onClick={() => {
                      setSelectedOption('create');
                      setStep(2);
                    }}
                  >
                    立即創建
                  </button>
                </div>
              ) : (
                <div className="wizard-partner-list">
                  {partners.map(partner => (
                    <div
                      key={partner.id}
                      className={`wizard-partner-item ${selectedPartnerId === partner.id ? 'selected' : ''}`}
                      onClick={() => setSelectedPartnerId(partner.id)}
                    >
                      <div className="partner-item-content">
                        <h4>{partner.name}</h4>
                        <p>{partner.description || '無描述'}</p>
                        <span className="partner-category">{partner.category || 'general'}</span>
                      </div>
                      {selectedPartnerId === partner.id && (
                        <div className="partner-check">
                          <Check size={20} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: 確認 */}
          {step === 3 && (
            <div className="wizard-step">
              <div className="wizard-summary">
                <div className="summary-item">
                  <Settings size={20} />
                  <div className="summary-content">
                    <span className="summary-label">設定方式</span>
                    <span className="summary-value">
                      {selectedOption === 'create' ? '創建新夥伴' : '選擇現有夥伴'}
                    </span>
                  </div>
                </div>

                <div className="summary-item">
                  <Sparkles size={20} />
                  <div className="summary-content">
                    <span className="summary-label">夥伴資訊</span>
                    <span className="summary-value">
                      {selectedOption === 'create' 
                        ? newPartnerData.name 
                        : partners.find(p => p.id === selectedPartnerId)?.name || '未選擇'}
                    </span>
                  </div>
                </div>

                <div className="summary-item">
                  <MessageCircle size={20} />
                  <div className="summary-content">
                    <span className="summary-label">功能</span>
                    <span className="summary-value">私訊專用 AI 夥伴</span>
                  </div>
                </div>
              </div>

              <div className="wizard-notice">
                <p>設定完成後，這個夥伴將會出現在您的私訊活動中，協助您進行一對一交流。</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="wizard-footer">
          <div className="wizard-footer-left">
            {step > 1 && (
              <button 
                className="wizard-btn-secondary" 
                onClick={handleBack}
                disabled={loading}
              >
                <ChevronLeft size={16} />
                上一步
              </button>
            )}
          </div>

          <div className="wizard-footer-right">
            {step === 1 && (
              <button 
                className="wizard-btn-text" 
                onClick={handleSkip}
                disabled={loading}
              >
                暫時跳過
              </button>
            )}
            
            {step < TOTAL_STEPS ? (
              <button 
                className="wizard-btn-primary" 
                onClick={handleNext}
                disabled={loading}
              >
                下一步
                <ChevronRight size={16} />
              </button>
            ) : (
              <button 
                className="wizard-btn-primary" 
                onClick={handleComplete}
                disabled={loading}
              >
                {loading ? '設定中...' : '完成設定'}
                <Check size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DmPartnerSetupWizard;
